import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { MotionConfig } from "motion/react";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLoader } from "@andrum/ui";
import { router } from "./routes";
import { useThemeStore } from "@/stores/theme-store";
import { useAuthStore } from "@/stores/auth-store";
import { flushOfflineQueue } from "@/lib/offline/flush";
import { registerDevPushIfPossible } from "@/features/notifications/api/push-api";
import { initPushNotifications } from "@/lib/capacitor/notifications";
import { configureRevenueCat } from "@/lib/billing/revenuecat";
import { maybeShowLocalReminder } from "@/lib/preferences/notification-prefs";
import {
  captureReferralFromUrl,
  claimStashedReferralIfAny,
} from "@/features/referrals/api/referrals-api";

function ThemeApplicator() {
  const { theme, setResolvedTheme } = useThemeStore();

  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia("(prefers-color-scheme: dark)");

    const apply = () => {
      const resolved =
        theme === "system" ? (media.matches ? "dark" : "light") : theme;
      setResolvedTheme(resolved);
      root.classList.toggle("dark", resolved === "dark");
    };

    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [theme, setResolvedTheme]);

  return null;
}

function AuthBootstrap({ children }: { children: ReactNode }) {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const status = useAuthStore((s) => s.status);
  const userId = useAuthStore((s) => s.user?.id);
  const [ready, setReady] = useState(bootstrapped);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      captureReferralFromUrl();
      await bootstrap();
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrap]);

  useEffect(() => {
    if (status !== "authenticated") return;
    void flushOfflineQueue();
    void registerDevPushIfPossible();
    void initPushNotifications();
    void maybeShowLocalReminder();
    void claimStashedReferralIfAny();
    if (userId) {
      void configureRevenueCat(userId);
    }
    const onOnline = () => {
      void flushOfflineQueue();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [status, userId]);

  const { t } = useTranslation();

  if (!ready) {
    return (
      <div className="flex min-h-full items-center justify-center bg-background">
        <PageLoader label={t("loading")} />
      </div>
    );
  }

  return children;
}

export function Providers({ children }: { children?: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            retry: 1,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion="user">
        <ThemeApplicator />
        <AuthBootstrap>
          <RouterProvider router={router} />
          {children}
        </AuthBootstrap>
      </MotionConfig>
    </QueryClientProvider>
  );
}
